import { useRef, useState } from "react";

import type { ListBox, Read, RowTask } from "@/components/ui/box.schema";
import type { RegisterPlanState, SessionRegister } from "@/lib/api/attendance";
import type { CourseUnit } from "@/lib/api/courseUnits";

import { ClassPlanCard } from "../ClassPlanCard";
import { UNPLACED_COPY } from "./session-copy";
import { placeName, type PlacedPlan } from "./use-plan-placement-tasks";

const copy = UNPLACED_COPY;

type UndatedPlan = SessionRegister["undated_plans"][number];

/** A row of Not on a session yet: a plan on no session, or the plan being made. */
export type UnplacedRow = { kind: "plan"; plan: UndatedPlan } | { kind: "new"; plan: UndatedPlan | undefined };

const PLAN_WORDS: Record<RegisterPlanState, string> = { draft: "Draft", changed: "Changed, not published", published: "Published" };

/** The register's read as this List's: the plans on no session, from the same snapshot (R1). */
function plansRead(read: Read<SessionRegister>, register: SessionRegister | undefined, rowsBeside: boolean): Read<UndatedPlan[]> {
  if (read.status === "stale") return { ...read, last: read.last.undated_plans };
  // A course with no sessions yet reads All sessions empty; its plans on no session are still read.
  if (read.status !== "ready" && read.status !== "empty") return read;
  const plans = register?.undated_plans ?? [];
  if (!plans.length && !rowsBeside) return { status: "empty", sentence: copy.none };
  return { status: "ready", value: plans, readAt: read.status === "ready" ? read.readAt : new Date().toISOString() };
}

/**
 * C08 Not on a session yet, for the teaching team: each plan made before its
 * session exists is a row, opened in place (K3) on its own plan editor, which
 * saves and publishes it. Putting it on a session is the row's own Task. A
 * plan it placed keeps its row and its result, named by the session it is on
 * now, until the page is left. New plan opens the plan being made as the
 * first row.
 */
export function useUnplacedPlansBox({ read, register, course, editable, openPlan, newPlanHref, sessionHref, place, onChanged, onNewClosed }: {
  read: Read<SessionRegister>;
  /** The register's last value, for the sessions a plan is on. */
  register: SessionRegister | undefined;
  course: { id: string; timezone: string; term_open: boolean };
  editable: boolean;
  /** The plan the address opens, or "new". */
  openPlan: string | null;
  newPlanHref: string;
  sessionHref: (sessionId: string) => string;
  /** The row's Task that puts its plan on a session. */
  place: (plan: PlacedPlan, rowKey: string) => RowTask;
  onChanged: () => void;
  onNewClosed: () => void;
}): ListBox<UndatedPlan[], UnplacedRow> {
  // The plan a New plan row created stays in that row until it closes, so it is never listed twice.
  const [createdId, setCreatedId] = useState<string | null>(null);
  // Each plan this List has drawn, so one it put on a session keeps its row and its result (W2).
  const drawn = useRef(new Map<string, UndatedPlan>());
  const showNew = editable && openPlan === "new";
  const placedOn = (plan: UndatedPlan) => register?.sessions.find((session) => session.plan?.id === plan.id);
  const task = (row: UnplacedRow) => (row.plan ? place({ id: row.plan.id, topic: row.plan.topic, state: row.plan.state, version: row.plan.updated_at }, row.kind === "new" ? "new" : row.plan.id) : undefined);
  const placed = (row: UnplacedRow) => task(row)?.result.state === "confirmed";
  const live = register?.undated_plans ?? [];
  for (const plan of live) drawn.current.set(plan.id, plan);
  const placedAway = [...drawn.current.values()].filter((plan) => !live.some((other) => other.id === plan.id) && plan.id !== createdId && placed({ kind: "plan", plan }));
  const card = (planId: string) => (
    <ClassPlanCard
      workspace={{ course, session: null }}
      canEdit={editable}
      presentation="embedded"
      headingLevel={4}
      planId={planId}
      createdPlanId={planId === "new" ? createdId : undefined}
      onSaved={(saved: CourseUnit) => {
        if (planId === "new") setCreatedId(saved.id);
        onChanged();
      }}
      onDiscarded={() => {
        if (planId === "new") {
          setCreatedId(null);
          onNewClosed();
        }
        onChanged();
      }}
    />
  );

  return {
    kind: "list",
    id: "unplaced-plans",
    job: copy.job,
    label: copy.label,
    owner: { system: "imam" },
    read: plansRead(read, register, showNew || placedAway.length > 0),
    fold: "never",
    state: (plans) => ({ class: "quiet", word: copy.count(plans.length) }),
    detail: () => "",
    openDetail: (plans) => (plans.length ? undefined : copy.none),
    exits: editable && !showNew ? [{ label: copy.newPlan, to: newPlanHref }] : undefined,
    rows: (plans) => {
      const created = createdId ? plans.find((plan) => plan.id === createdId) ?? drawn.current.get(createdId) : undefined;
      return [
        // Once saved, the plan being made keeps its row and its editor, under its own title and state.
        ...(showNew ? [{ kind: "new", plan: created } as const] : []),
        ...[...plans, ...placedAway].filter((plan) => plan.id !== createdId).map((plan) => ({ kind: "plan", plan }) as const),
      ];
    },
    row: {
      key: (row) => (row.kind === "new" ? "new" : row.plan.id),
      title: (row) => row.plan?.topic ?? copy.newPlan,
      nav: (row) => {
        const session = row.plan && placed(row) ? placedOn(row.plan) : undefined;
        return session ? sessionHref(session.id) : undefined;
      },
      state: (row) => {
        if (!row.plan) return { class: "draft", word: copy.notSaved };
        const session = placed(row) ? placedOn(row.plan) : undefined;
        if (session) return { class: "done", word: copy.on(placeName(session, course.timezone)) };
        return { class: row.plan.state === "published" ? "quiet" : "draft", word: PLAN_WORDS[row.plan.state] };
      },
      act: () => ({ verb: editable ? copy.edit : copy.read, does: "open" }),
      startsOpen: (row) => (row.kind === "new" ? true : row.plan.id === openPlan),
      // A placed plan's editor is its session's now: its row keeps only the result.
      detail: (row) => (placed(row) ? null : card(row.kind === "new" ? "new" : row.plan.id)),
      task: (row) => {
        if (!editable) return undefined;
        const own = task(row);
        return own && own.result.state === "confirmed" ? { ...own, parts: [] } : own;
      },
    },
  };
}
